/**
 * CalcTooltip — Reusable dark-themed tooltip showing calculation breakdowns.
 * Line 1: formula name (e.g. "Actual + Open Orders")
 * Line 2: actual values plugged in (e.g. "= $1,883,711 + $521,863")
 *
 * Uses a portal to render at document body level so tooltips are never
 * clipped by parent overflow:hidden containers (tables, cards, etc.).
 */

import {
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  ReactNode,
} from "react";
import { createPortal } from "react-dom";

interface CalcTooltipProps {
  /** Formula description (line 1) — e.g. "Actual ÷ Budget (Prorated)" */
  formula: string;
  /** Substituted values (line 2) — e.g. "= $1,883,711 ÷ $3,211,000 = 58.6%" */
  values: string;
  /** The element that triggers the tooltip on hover */
  children: ReactNode;
}

export function CalcTooltip({ formula, values, children }: CalcTooltipProps) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const ttRect = tooltipRef.current.getBoundingClientRect();
    const left = rect.left + rect.width / 2 - ttRect.width / 2;
    const top = rect.top - ttRect.height - 6;
    setPos({
      top: top < 4 ? rect.bottom + 6 : top,
      left: Math.max(4, Math.min(left, window.innerWidth - ttRect.width - 4)),
    });
  }, []);

  useLayoutEffect(() => {
    if (visible) updatePosition();
  }, [visible, updatePosition]);

  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => setVisible(true), 200);
  };

  const handleMouseLeave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(false);
  };

  return (
    <span
      ref={triggerRef}
      className="inline-block cursor-help"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {visible &&
        createPortal(
          <div
            ref={tooltipRef}
            className="fixed z-[9999] pointer-events-none"
            style={{ top: pos.top, left: pos.left }}
          >
            <span className="block bg-navy-900 text-white text-xs rounded-lg shadow-pop px-3 py-2">
              <span className="block text-wmp-200 font-medium">{formula}</span>
              <span className="block text-white font-semibold tabular-nums mt-0.5">
                {values}
              </span>
            </span>
            {/* Caret pointing down */}
            <span
              className="block mx-auto"
              style={{
                width: 0,
                height: 0,
                borderLeft: "5px solid transparent",
                borderRight: "5px solid transparent",
                borderTop: "5px solid #101D33",
              }}
            />
          </div>,
          document.body,
        )}
    </span>
  );
}
